<template>
  <van-popup v-model:show="visible" position="bottom" round :close-on-click-overlay="closeOnClickOverlay"
    :teleport="teleport" class="custom-confirm-popup" @close="handleClose">
    <div class="confirm-wrapper">
      <div v-if="showClose" class="confirm-close" @click="handleCancel">
        <van-icon name="cross" size="18" />
      </div>
      <div v-if="title" class="confirm-title">
        <div class="confirm-title-text">
          {{ title }}
        </div>
        <div v-if="subtitle" class="confirm-subtitle">{{ subtitle }}</div>
      </div>
      <div class="confirm-body">
        <div v-if="icon" class="confirm-icon">
          <van-icon v-if="icon" :name="icon" :size="iconSize" :color="iconColor" class="confirm-icon" />
        </div>
        <slot name="message">
          <div v-if="message" class="confirm-message">{{ message }}</div>
        </slot>
      </div>

      <div class="confirm-footer">
        <slot name="footer">
          <van-button block round class="confirm-btn confirm-btn--confirm" @click="handleConfirm">
            {{ confirmText }}
          </van-button>
          <van-button v-if="showCancelButton" block round class="confirm-btn confirm-btn--cancel" @click="handleCancel">
            {{ cancelText }}
          </van-button>

        </slot>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
/**
 * Confirm 确认弹窗组件
 * @description 从底部向上抽出的确认弹窗，支持图标+标题、自定义按钮文案、Slot/JSX 自定义内容
 * @property {boolean} modelValue - 控制显示/隐藏
 * @property {string} title - 标题文案
 * @property {string} subtitle - 副标题文案
 * @property {string} message - 内容文案
 * @property {string} icon - Vant 图标名称
 * @property {string} iconSize - 图标大小
 * @property {string} iconColor - 图标颜色
 * @property {boolean} showClose - 是否显示右上角关闭按钮
 * @property {boolean} showCancelButton - 是否显示取消按钮
 * @property {string} cancelText - 取消按钮文案
 * @property {string} confirmText - 确认按钮文案
 * @property {boolean} closeOnClickOverlay - 点击遮罩是否关闭
 * @slot message - 自定义内容区域
 * @slot footer - 自定义底部按钮区域
 * @emits confirm - 确认回调
 * @emits cancel - 取消/关闭回调
 */
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  subtitle: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  iconSize: {
    type: String,
    default: '48px',
  },
  iconColor: {
    type: String,
    default: '#ee0a24',
  },
  showClose: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  cancelText: {
    type: String,
    default: 'Cancel',
  },
  confirmText: {
    type: String,
    default: 'Confirm',
  },
  closeOnClickOverlay: {
    type: Boolean,
    default: false,
  },
  teleport: {
    type: String,
    default: '#cricket-app',
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => {
    emit('update:modelValue', val)
  },
})

const handleConfirm = () => {
  visible.value = false
  emit('confirm')
}

const handleCancel = () => {
  visible.value = false
  emit('cancel')
}

const handleClose = () => {
  emit('close')
}
</script>

<style scoped lang="less">
.custom-confirm-popup {
  max-width: 750px;
}

.confirm-wrapper {
  position: relative;
  padding: 0px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
}

.confirm-close {
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #999;
  cursor: pointer;
}

.confirm-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
}

.confirm-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  height: 60px;
  border-bottom: 1px solid #dfe3e8 !important;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
}

.confirm-title-text {
  font-size: 16px;
}

.confirm-subtitle {
  font-size: 14px;
  color: #505d74;
  font-family: Heebo, sans-serif;
  font-weight: 400;
}

.confirm-body {
  margin-bottom: 28px;
}

.confirm-message {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  text-align: center;
  word-break: break-word;
  margin-bottom: 12px;
  margin-top: 12px;
}

.confirm-footer {
  display: flex;
  gap: 12px;
  flex-direction: column;
  padding: 0px 15px;
}

.confirm-btn {
  flex: 1;
  height: 40px;
  font-size: 14px;
  text-transform: uppercase;
  font-family: Heebo-Bold !important;
}

.confirm-btn--cancel {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #dfe3e8 !important;
  border-radius: 5px !important;
  background-color: #f7f9fb !important;
  color: #202b3c !important;
}

.confirm-btn--confirm {
  background: var(--van-success-color);
  padding: 10px;
  color: #fff;
  border: none;
  border-radius: 5px;
}
</style>
